import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, RadialBarChart, RadialBar
} from 'recharts';
import { 
  Shield, CheckCircle2, XCircle, AlertTriangle, Clock, FileText,
  ClipboardCheck, Users, Calendar, Building, RefreshCw, Download,
  Eye, EyeOff, Zap, ArrowRight, Folder, FileCheck, FileWarning,
  Lock, Unlock, Sparkles, Target, Upload, Loader2, History,
  Plus, Trash2, Link, FileSpreadsheet, FileBarChart, TrendingUp
} from 'lucide-react';
import { 
  PageHeader, MetricCard, StatusBadge, ModernCard, SectionHeader,
  modernTabsClass, modernTabTriggerClass, GradientProgress
} from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Audit categories with checklist items
const AUDIT_CATEGORIES = {
  documentation: {
    name: 'Documentation',
    icon: FileText,
    items: [
      { id: 'doc-1', item: 'Plan of Care updated within 15 days', required: true },
      { id: 'doc-2', item: 'CTI completed before billing', required: true },
      { id: 'doc-3', item: 'Face-to-Face before day 180 recert', required: true },
      { id: 'doc-4', item: 'IDG meeting documentation complete', required: true },
      { id: 'doc-5', item: 'Nursing assessments within 48 hours', required: true },
      { id: 'doc-6', item: 'Visit notes signed and dated', required: false }
    ]
  },
  clinical: {
    name: 'Clinical Care',
    icon: Users,
    items: [
      { id: 'clin-1', item: 'Pain management protocol documented', required: true },
      { id: 'clin-2', item: 'Symptom management plans in place', required: true },
      { id: 'clin-3', item: 'Medication reconciliation complete', required: true },
      { id: 'clin-4', item: 'Care coordination documented', required: false },
      { id: 'clin-5', item: 'Bereavement services offered', required: true }
    ]
  },
  compliance: {
    name: 'Compliance',
    icon: Shield,
    items: [
      { id: 'comp-1', item: 'HIPAA training current for all staff', required: true },
      { id: 'comp-2', item: 'Background checks completed', required: true },
      { id: 'comp-3', item: 'Licensure verification current', required: true },
      { id: 'comp-4', item: 'Incident reports filed within 24 hours', required: true },
      { id: 'comp-5', item: 'Quality metrics reported monthly', required: false }
    ]
  },
  safety: {
    name: 'Safety',
    icon: AlertTriangle,
    items: [
      { id: 'safe-1', item: 'Fall risk assessments documented', required: true },
      { id: 'safe-2', item: 'Infection control protocols followed', required: true },
      { id: 'safe-3', item: 'Emergency procedures current', required: true },
      { id: 'safe-4', item: 'Equipment safety checks documented', required: false }
    ]
  },
  training: {
    name: 'Training',
    icon: Building,
    items: [
      { id: 'train-1', item: 'Annual competency evaluations complete', required: true },
      { id: 'train-2', item: 'Orientation records for new hires', required: true },
      { id: 'train-3', item: 'Continuing education hours met', required: false },
      { id: 'train-4', item: 'CPR certification current', required: true }
    ]
  }
};

export default function AuditReadyView() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [auditMode, setAuditMode] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  
  // Document management state
  const [documents, setDocuments] = useState([]);
  const [docSummary, setDocSummary] = useState(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  
  // Audit history state
  const [auditHistory, setAuditHistory] = useState([
    { id: 1, date: '2025-08-15', type: 'CHAP Survey', result: 'Passed', score: 94, findings: 3, resolved: 3 },
    { id: 2, date: '2025-05-20', type: 'Internal Audit', result: 'Passed', score: 91, findings: 5, resolved: 5 },
    { id: 3, date: '2025-02-10', type: 'State Survey', result: 'Passed', score: 88, findings: 7, resolved: 7 },
    { id: 4, date: '2024-11-05', type: 'CHAP Survey', result: 'Passed', score: 85, findings: 10, resolved: 10 }
  ]);
  const [exporting, setExporting] = useState(false);

  // Fetch documents from API
  const fetchDocuments = async () => {
    setLoadingDocs(true);
    try {
      const response = await fetch(`${API_URL}/api/simulations/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
        setDocSummary(data.summary);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Update document status
  const updateDocumentStatus = async (docId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/simulations/documents/${docId}`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success('Document status updated');
        fetchDocuments();
      }
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  // Handle file upload
  const handleFileUpload = async (docId, file) => {
    setUploadingDoc(docId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/simulations/documents/${docId}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('File uploaded successfully');
        fetchDocuments();
      } else {
        toast.error('Upload failed');
      }
    } catch (error) {
      toast.error('Upload error');
    } finally {
      setUploadingDoc(null);
    }
  };

  // Initialize checklist state
  useEffect(() => {
    const initialChecklist = {};
    Object.entries(AUDIT_CATEGORIES).forEach(([catKey, category]) => {
      category.items.forEach(item => {
        // Randomly set some items as complete for demo
        initialChecklist[item.id] = Math.random() > 0.3;
      });
    });
    setChecklist(initialChecklist);
    setLoading(false);
    fetchDocuments();
  }, []);

  const toggleCheckItem = (itemId) => {
    setChecklist(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Calculate readiness scores
  const calculateCategoryScore = (catKey) => {
    const items = AUDIT_CATEGORIES[catKey].items;
    const completed = items.filter(item => checklist[item.id]).length;
    return Math.round((completed / items.length) * 100);
  };

  const calculateOverallScore = () => {
    let totalItems = 0;
    let completedItems = 0;
    Object.values(AUDIT_CATEGORIES).forEach(category => {
      category.items.forEach(item => {
        totalItems++;
        if (checklist[item.id]) completedItems++;
      });
    });
    return Math.round((completedItems / totalItems) * 100);
  };

  const calculateRequiredScore = () => {
    let totalRequired = 0;
    let completedRequired = 0;
    Object.values(AUDIT_CATEGORIES).forEach(category => {
      category.items.filter(i => i.required).forEach(item => {
        totalRequired++;
        if (checklist[item.id]) completedRequired++;
      });
    });
    return Math.round((completedRequired / totalRequired) * 100);
  };

  const overallScore = calculateOverallScore();
  const requiredScore = calculateRequiredScore();

  // Prepare chart data
  const categoryScores = Object.entries(AUDIT_CATEGORIES).map(([key, cat]) => ({
    name: cat.name,
    score: calculateCategoryScore(key),
    fill: calculateCategoryScore(key) >= 80 ? '#10B981' : calculateCategoryScore(key) >= 60 ? '#F59E0B' : '#EF4444'
  }));

  const pieData = [
    { name: 'Complete', value: overallScore, fill: '#10B981' },
    { name: 'Incomplete', value: 100 - overallScore, fill: '#E5E7EB' }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${auditMode ? 'ring-2 ring-emerald-500 rounded-2xl p-4 bg-emerald-50/30 dark:bg-emerald-900/10' : ''}`} data-testid="audit-ready-page">
      {/* Header */}
      <PageHeader
        icon={Shield}
        title="Audit-Ready View"
        subtitle="Comprehensive compliance monitoring and survey preparation"
        gradient="from-emerald-600 via-teal-600 to-cyan-600"
      >
        <div className="flex items-center gap-4 bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
          <span className="text-white text-sm font-medium">Audit Mode</span>
          <Switch
            checked={auditMode}
            onCheckedChange={setAuditMode}
            className="data-[state=checked]:bg-white"
          />
          {auditMode ? (
            <Eye className="h-5 w-5 text-white" />
          ) : (
            <EyeOff className="h-5 w-5 text-white/60" />
          )}
        </div>
        <Button 
          variant="secondary"
          className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
          onClick={async () => {
            setExporting(true);
            try {
              toast.success('Audit report export started. Download will begin shortly.');
              // Simulate export
              await new Promise(resolve => setTimeout(resolve, 1500));
              toast.success('Audit report downloaded successfully');
            } catch (error) {
              toast.error('Export failed');
            } finally {
              setExporting(false);
            }
          }}
          disabled={exporting}
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export Report
        </Button>
      </PageHeader>

      {/* Audit Mode Banner */}
      {auditMode && (
        <Card className="bg-emerald-500 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Lock className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-bold">Audit Mode Active</p>
                  <p className="text-emerald-100 text-sm">All compliance documentation is highlighted and accessible</p>
                </div>
              </div>
              <Button variant="secondary" className="bg-white text-emerald-600">
                <FileCheck className="h-4 w-4 mr-2" />
                Quick Access Panel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Overall Readiness"
          value={`${overallScore}%`}
          icon={Target}
          gradient={overallScore >= 80 ? 'emerald' : overallScore >= 60 ? 'amber' : 'rose'}
          subtitle="All categories"
        />
        <MetricCard
          title="Required Items"
          value={`${requiredScore}%`}
          icon={Shield}
          gradient={requiredScore >= 90 ? 'emerald' : requiredScore >= 70 ? 'amber' : 'rose'}
          subtitle="Critical compliance"
        />
        <MetricCard
          title="Categories Ready"
          value={`${categoryScores.filter(c => c.score >= 80).length}/${categoryScores.length}`}
          icon={CheckCircle2}
          gradient="blue"
          subtitle="At 80%+ completion"
        />
        <MetricCard
          title="Days Since Last Audit"
          value="127"
          icon={Calendar}
          gradient="purple"
          subtitle="Annual audit due in 238 days"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="overview" className={modernTabTriggerClass}>
            <Target className="h-4 w-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="checklist" className={modernTabTriggerClass}>
            <ClipboardCheck className="h-4 w-4 mr-2" /> Checklist
          </TabsTrigger>
          <TabsTrigger value="documents" className={modernTabTriggerClass}>
            <Folder className="h-4 w-4 mr-2" /> Documents
          </TabsTrigger>
          <TabsTrigger value="gaps" className={modernTabTriggerClass}>
            <FileWarning className="h-4 w-4 mr-2" /> Gaps Analysis
          </TabsTrigger>
          <TabsTrigger value="history" className={modernTabTriggerClass}>
            <History className="h-4 w-4 mr-2" /> Audit History
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Readiness Gauge */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Target} title="Audit Readiness Score" />
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center">
                  <ResponsiveContainer width={200} height={200}>
                    <RadialBarChart
                      cx="50%"
                      cy="50%"
                      innerRadius="60%"
                      outerRadius="100%"
                      barSize={20}
                      data={[{ value: overallScore, fill: overallScore >= 80 ? '#10B981' : overallScore >= 60 ? '#F59E0B' : '#EF4444' }]}
                      startAngle={90}
                      endAngle={-270}
                    >
                      <RadialBar background clockWise dataKey="value" />
                    </RadialBarChart>
                  </ResponsiveContainer>
                  <div className="absolute text-center">
                    <p className="text-4xl font-bold">{overallScore}%</p>
                    <p className="text-sm text-muted-foreground">Ready</p>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <StatusBadge status={overallScore >= 80 ? 'HEALTHY' : overallScore >= 60 ? 'MODERATE' : 'CRITICAL'} size="lg" />
                </div>
              </CardContent>
            </ModernCard>

            {/* Category Breakdown */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Shield} title="Category Scores" />
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={categoryScores} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="name" width={100} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                      {categoryScores.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </ModernCard>
          </div>

          {/* Quick Actions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {categoryScores.filter(c => c.score < 80).slice(0, 3).map((cat, idx) => (
              <Card key={idx} className="border-amber-200 bg-amber-50/50 dark:bg-amber-900/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{cat.name}</p>
                      <p className="text-sm text-muted-foreground">{cat.score}% complete</p>
                    </div>
                    <Button size="sm" variant="outline">
                      Review <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Checklist Tab */}
        <TabsContent value="checklist" className="space-y-6">
          {Object.entries(AUDIT_CATEGORIES).map(([catKey, category]) => {
            const Icon = category.icon;
            const score = calculateCategoryScore(catKey);
            
            return (
              <ModernCard key={catKey}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${score >= 80 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                        <Icon className={`h-5 w-5 ${score >= 80 ? 'text-emerald-600' : 'text-amber-600'}`} />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{category.name}</CardTitle>
                        <CardDescription>{category.items.length} items</CardDescription>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${score >= 80 ? 'text-emerald-600' : score >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                        {score}%
                      </p>
                      <Progress value={score} className="w-24 h-2" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {category.items.map((item) => (
                      <div 
                        key={item.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          checklist[item.id] ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-slate-50 dark:bg-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={checklist[item.id]}
                            onCheckedChange={() => toggleCheckItem(item.id)}
                          />
                          <span className={checklist[item.id] ? 'text-muted-foreground line-through' : ''}>
                            {item.item}
                          </span>
                          {item.required && (
                            <Badge variant="outline" className="text-xs">Required</Badge>
                          )}
                        </div>
                        {checklist[item.id] ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-slate-300" />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </ModernCard>
            );
          })}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6">
          {/* Document Summary */}
          {docSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                <CardContent className="p-4">
                  <p className="text-sm opacity-80">Total Documents</p>
                  <p className="text-2xl font-bold">{docSummary.total}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                <CardContent className="p-4">
                  <p className="text-sm opacity-80">Current</p>
                  <p className="text-2xl font-bold">{docSummary.current}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                <CardContent className="p-4">
                  <p className="text-sm opacity-80">Needs Update</p>
                  <p className="text-2xl font-bold">{docSummary.needs_update}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-[#00b0ab] to-[#008a86] text-white">
                <CardContent className="p-4">
                  <p className="text-sm opacity-80">Compliance Rate</p>
                  <p className="text-2xl font-bold">{docSummary.compliance_rate}%</p>
                </CardContent>
              </Card>
            </div>
          )}

          <ModernCard>
            <CardHeader>
              <div className="flex items-center justify-between">
                <SectionHeader icon={Folder} title="Compliance Documents" subtitle="Upload and manage audit-ready documents" />
                <Button variant="outline" onClick={fetchDocuments} disabled={loadingDocs}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingDocs ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingDocs ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {documents.map((doc, idx) => (
                    <Card 
                      key={doc.id || idx} 
                      className={`hover:shadow-md transition-all ${
                        doc.status === 'needs_update' ? 'border-amber-300' : 
                        doc.status === 'expired' ? 'border-red-300' : ''
                      }`}
                    >
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className={`h-5 w-5 ${
                              doc.status === 'needs_update' ? 'text-amber-500' : 
                              doc.status === 'expired' ? 'text-red-500' : 'text-blue-500'
                            }`} />
                            <div>
                              <p className="font-medium text-sm">{doc.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {doc.category} • Updated: {doc.last_reviewed || 'N/A'}
                              </p>
                            </div>
                          </div>
                          <Badge className={`text-xs ${
                            doc.status === 'current' ? 'bg-emerald-500' :
                            doc.status === 'needs_update' ? 'bg-amber-500' : 'bg-red-500'
                          } text-white`}>
                            {doc.status === 'current' ? 'Current' : doc.status === 'needs_update' ? 'Update' : 'Expired'}
                          </Badge>
                        </div>
                        
                        {/* File info */}
                        {doc.file_name && (
                          <div className="text-xs text-muted-foreground bg-slate-50 dark:bg-slate-800 p-2 rounded">
                            <FileCheck className="h-3 w-3 inline mr-1" />
                            {doc.file_name} ({Math.round((doc.file_size || 0) / 1024)}KB)
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-2 border-t">
                          <label className="flex-1">
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleFileUpload(doc.id, e.target.files[0]);
                                }
                              }}
                            />
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="w-full"
                              disabled={uploadingDoc === doc.id}
                              asChild
                            >
                              <span>
                                {uploadingDoc === doc.id ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <Upload className="h-3 w-3 mr-1" />
                                )}
                                Upload
                              </span>
                            </Button>
                          </label>
                          {doc.status !== 'current' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateDocumentStatus(doc.id, 'current')}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Mark Current
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Gaps Analysis Tab */}
        <TabsContent value="gaps">
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={FileWarning} title="Compliance Gaps Analysis" subtitle="Items requiring immediate attention" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(AUDIT_CATEGORIES).flatMap(([catKey, category]) => 
                  category.items
                    .filter(item => !checklist[item.id] && item.required)
                    .map(item => ({
                      ...item,
                      category: category.name
                    }))
                ).map((item, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-lg border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge variant="outline" className="mb-2">{item.category}</Badge>
                        <p className="font-medium">{item.item}</p>
                        <p className="text-sm text-red-600 dark:text-red-400 mt-1">Required for compliance</p>
                      </div>
                      <Button size="sm" variant="outline">
                        Address Now
                      </Button>
                    </div>
                  </div>
                ))}
                {Object.entries(AUDIT_CATEGORIES).flatMap(([catKey, category]) => 
                  category.items.filter(item => !checklist[item.id] && item.required)
                ).length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                    <p className="text-lg font-medium text-emerald-600">All Required Items Complete!</p>
                    <p className="text-sm mt-2">Your organization is audit-ready for critical compliance items</p>
                  </div>
                )}
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Audit History Tab */}
        <TabsContent value="history" className="space-y-6">
          {/* Timeline */}
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={History} title="Audit History Timeline" subtitle="Track your compliance journey" />
            </CardHeader>
            <CardContent>
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />
                
                <div className="space-y-6">
                  {auditHistory.map((audit, idx) => (
                    <div key={audit.id} className="relative flex items-start gap-4 pl-14">
                      {/* Timeline dot */}
                      <div className={`absolute left-4 w-4 h-4 rounded-full border-2 ${
                        audit.result === 'Passed' 
                          ? 'bg-emerald-500 border-emerald-200' 
                          : 'bg-red-500 border-red-200'
                      }`} />
                      
                      <div className="flex-1 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl hover:shadow-md transition-all">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={audit.result === 'Passed' ? 'bg-emerald-500' : 'bg-red-500'}>
                                {audit.result}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {new Date(audit.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </span>
                            </div>
                            <h4 className="font-semibold text-lg">{audit.type}</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              Score: {audit.score}% | Findings: {audit.findings} | Resolved: {audit.resolved}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className={`text-3xl font-bold ${
                              audit.score >= 90 ? 'text-emerald-600' : 
                              audit.score >= 80 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {audit.score}%
                            </div>
                            <p className="text-xs text-muted-foreground">Compliance Score</p>
                          </div>
                        </div>
                        
                        {/* Findings details */}
                        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 text-amber-500" />
                              <span className="text-sm">{audit.findings} Findings</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                              <span className="text-sm">{audit.resolved} Resolved</span>
                            </div>
                            <div className="flex-1" />
                            <Button size="sm" variant="outline">
                              <FileText className="h-4 w-4 mr-1" />
                              View Report
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </ModernCard>

          {/* Export Options */}
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={Download} title="Export Audit Reports" subtitle="Download compliance documentation" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex-col gap-2"
                  onClick={() => toast.success('PDF export started')}
                >
                  <FileText className="h-8 w-8 text-red-500" />
                  <span className="font-medium">Export as PDF</span>
                  <span className="text-xs text-muted-foreground">Full audit report with charts</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex-col gap-2"
                  onClick={() => toast.success('Excel export started')}
                >
                  <FileSpreadsheet className="h-8 w-8 text-emerald-500" />
                  <span className="font-medium">Export as Excel</span>
                  <span className="text-xs text-muted-foreground">Checklist data and metrics</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex-col gap-2"
                  onClick={() => toast.success('CSV export started')}
                >
                  <FileBarChart className="h-8 w-8 text-blue-500" />
                  <span className="font-medium">Export as CSV</span>
                  <span className="text-xs text-muted-foreground">Raw compliance data</span>
                </Button>
              </div>
            </CardContent>
          </ModernCard>

          {/* Compliance Trend */}
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={TrendingUp} title="Compliance Trend" subtitle="Track improvement over time" />
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={[...auditHistory].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'Score']}
                    labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  />
                  <Legend />
                  <Bar dataKey="score" name="Compliance Score" fill="#6366F1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </ModernCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
